import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { guestLikeRoles, userRoles, userTypes } from 'utils/constants';
import { OrgPermissions, moduleKeys } from 'utils/permissions';
import { RESI_ROUTER_NAMESPACE } from './ResiGuard';
import menuItem from 'menu-items';
import { getFirstAccessibleUrl } from 'utils/accessible-menu';
import {
    clearOrgSelectorSwitch,
    getPendingOrgSelectorDestination,
    getPendingOrgSelectorSwitch
} from 'utils/org-switch-intent';
import { Loader } from 'components';

// How long the root waits for an org selector switch to land before choosing a page anyway,
// e.g. when the switch failed. Shorter than the pick's own expiry.
const WAIT_FOR_SWITCH_MS = 8000;

export const getRouteForUser = (userData, activeOrg) => {
    if (userData.type === userTypes.SUPER_ADMIN) {
        return 'health-dashboard';
    }

    if (userData.role === 'operator') {
        const activeOrgId = userData.active_org_id;
        const activeUserOrg = userData.orgs?.[activeOrgId];

        if (activeUserOrg?.permissions) {
            const permissions = activeUserOrg.permissions;

            // Every landing page below needs the org to have its module as well as the user to
            // hold the permission (RouteGuard checks both), so a permission alone must not pick
            // it: an org without the module would send the user to a 404. activeOrg is still
            // null on a fresh page load or right after an org switch (its Firestore listener
            // hasn't delivered a snapshot yet), so wait rather than guess.
            if (activeOrg === null) {
                return null;
            }

            const orgHasModule = moduleKey =>
                Boolean(activeOrg?.modules?.includes(moduleKey));

            if (
                permissions.includes(OrgPermissions.ORG_DASHBOARD) &&
                orgHasModule(moduleKeys.ORG_DASHBOARD)
            ) {
                return 'dashboard';
            }
            if (
                permissions.includes(OrgPermissions.VIRTUAL_GUARD) &&
                orgHasModule(moduleKeys.VIRTUAL_GUARD)
            ) {
                return 'virtual-guard';
            }
            if (permissions.includes(OrgPermissions.RENTAL_MANAGER)) {
                // Same permission gates both the legacy /tenant-lookup route (module
                // RENTAL_MANAGER) and the newer /rentals route (module
                // SHORT_TERM_RENTAL) — see MainRoutes.js. Default to whichever module
                // the org actually has enabled; if only Short-Term Rental is on (legacy
                // switched off), land there instead of the now-inaccessible legacy page.
                // Any other combination (both on, neither on) keeps the long-standing
                // legacy default.
                //
                // activeOrg is still null on a fresh page load (its Firestore listener
                // hasn't delivered a snapshot yet) — return null ("not ready to decide")
                // rather than guessing the legacy default: UserGuard only wraps
                // '/', '/login', etc., so it unmounts the instant it navigates and never
                // gets a second chance to correct a wrong guess once RouteGuard 404s it.
                if (activeOrg === null) {
                    return null;
                }

                const hasLegacyRentalManager = activeOrg?.modules?.includes(
                    moduleKeys.RENTAL_MANAGER
                );
                const hasShortTermRental = activeOrg?.modules?.includes(
                    moduleKeys.SHORT_TERM_RENTAL
                );

                if (hasShortTermRental && !hasLegacyRentalManager) {
                    return 'rentals';
                }
                return 'tenant-lookup';
            }
            if (permissions.includes(OrgPermissions.LPR) && orgHasModule(moduleKeys.LPR)) {
                return 'plate-search';
            }

            // None of the pages above fit this org: land on the first one its sidebar shows,
            // rather than on a dashboard it may not have.
            const firstAccessibleUrl = getFirstAccessibleUrl({
                menuGroups: menuItem.items,
                activeOrg,
                permissions,
                userData
            });

            if (firstAccessibleUrl) {
                return firstAccessibleUrl;
            }
        }

        return 'dashboard';
    }

    const activePropertyId = userData.active_property_id;
    const activeProperty = userData.properties?.[activePropertyId];
    const propertyRole = activeProperty?.role;

    if (propertyRole === userRoles.RESIDENT || propertyRole === userRoles.TENANT) {
        return `${RESI_ROUTER_NAMESPACE}/guests`;
    }

    if (guestLikeRoles.includes(propertyRole)) {
        if (userData.photo_id === null) {
            return `${RESI_ROUTER_NAMESPACE}/upload-identification`;
        }

        return `${RESI_ROUTER_NAMESPACE}/access-keys`;
    }
    if (
        userData?.role === userRoles.RESIDENT ||
        userData?.role === userRoles.TENANT ||
        guestLikeRoles.includes(userData?.role)
    ) {
        return RESI_ROUTER_NAMESPACE;
    }
    return 'dashboard';
};

const UserGuard = ({ children, authUser, userData, activeOrg }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [, setWaitedLongEnough] = useState(0);

    // The org selector sends the user here the moment they pick an org, so the page they were on
    // is gone before the org changes (it may not exist in the new one). The page is chosen once
    // the pick has landed, from the new org and the user document together, never before.
    const pendingOrgId = getPendingOrgSelectorSwitch();
    const switchLanded =
        Boolean(pendingOrgId) &&
        userData?.active_org_id === pendingOrgId &&
        activeOrg?.org_id === pendingOrgId;
    const waitingForSwitch = Boolean(pendingOrgId) && !switchLanded;

    // A switch that never lands (it failed) must not leave the user waiting here for good.
    useEffect(() => {
        if (!waitingForSwitch) return undefined;

        const timer = setTimeout(() => {
            clearOrgSelectorSwitch();
            setWaitedLongEnough(count => count + 1);
        }, WAIT_FOR_SWITCH_MS);

        return () => clearTimeout(timer);
    }, [waitingForSwitch]);

    useEffect(() => {
        if (waitingForSwitch) return;

        if (authUser && userData) {
            // A switch that was started for a page (a notification action) opens that page
            // instead of the org's default one. Read before the intent is cleared.
            const switchDestination = switchLanded
                ? getPendingOrgSelectorDestination()
                : null;

            if (switchLanded) {
                clearOrgSelectorSwitch();
            }

            // Short-circuits before touching activeOrg when there's an explicit
            // redirect-back target, so a `from` override never gets stuck waiting on it.
            const targetRoute =
                switchDestination ||
                location?.state?.from ||
                getRouteForUser(userData, activeOrg);

            // null means getRouteForUser isn't ready to decide yet (waiting on
            // activeOrg) — skip navigating this run; the effect re-fires once
            // activeOrg lands.
            if (!targetRoute) return;

            navigate(targetRoute, {
                state: {
                    from: ''
                },
                replace: true
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authUser, userData, activeOrg, navigate, location?.pathname, waitingForSwitch]);

    return waitingForSwitch ? <Loader /> : children;
};

UserGuard.propTypes = {
    children: PropTypes.node,
    authUser: PropTypes.object,
    userData: PropTypes.object,
    activeOrg: PropTypes.object
};

const mapStateToProps = ({ auth, user }) => {
    const { authUser } = auth;
    const { userData, activeOrg } = user;
    return { authUser, userData, activeOrg };
};

export default connect(mapStateToProps, {})(UserGuard);
